/**
 * EnvironmentalMeasurementForm Component
 * Formulário de cadastro e edição de medição ambiental (#351)
 *
 * O excedente é informado pelo técnico. O limite de tolerância e a unidade do
 * risco aparecem ao lado do campo como apoio à decisão — a tela não compara o
 * valor medido com o limite (D2 do plano: o limite é texto livre e só é
 * comparável numa fatia dos riscos).
 *
 * Exceção (#459): nos riscos de sílica livre o limite tem fórmula. O técnico digita o % de
 * quartzo e a tela mostra o limite calculado e a faixa; o catálogo de limites e o botão de
 * excedente saem, porque quem decide é a conta, e o servidor a refaz ao gravar.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  COMFORT_PARAMETERS,
  EnvironmentalMeasurementFormData,
  EnvironmentalMeasurementFormProps,
  MeasuredRiskContext,
  MEASUREMENT_ASSESSMENT_TYPES,
  MEASUREMENT_ASSESSMENT_TYPE_LABELS,
  NR16_ANNEXES,
} from '../../../types/environmentalMeasurement';
import type { RiskApplication, RiskApplicationScope } from '../../../types/riskApplication';
import {
  UNHEALTHY_DEGREES,
  OCC_UNHEALTHY_DEGREE_LABELS,
  type UnhealthyDegree,
} from '../../../types/occupationalRisk';
import { riskApplicationService } from '../../../services/riskApplicationService';
import { measurementEquipmentService } from '../../../services/measurementEquipmentService';
import { companyService } from '../../../services/companyService';
import { departmentService } from '../../../services/departmentService';
import { occupationService } from '../../../services/occupationService';
import { employeeService } from '../../../services/employeeService';
import { userService } from '../../../services/userService';
import { occupationalRiskService } from '../../../services/occupationalRiskService';
import type { OccupationalRisk } from '../../../types/occupationalRisk';
import { useDadoPermitido } from '../../../authorization/useListaPermitida';
import {
  hazardousAgentService,
  type HazardousAgentSheet,
  type HazardousAgentToleranceLimit,
} from '../../../services/hazardousAgentService';
import { useVeredito } from '../../../authorization/useVeredito';
import { pode } from '../../../authorization/pode';
import { useCompanyContext } from '../../../hooks/useCompanyContext';
import { BlocoDoLimiteCalculado } from '../../../components/measurements/BlocoDoLimiteCalculado';

interface Option {
  id: number;
  name: string;
}

const SCOPE_OPTIONS: Array<{ value: RiskApplicationScope; label: string }> = [
  { value: 'company', label: 'Empresa' },
  { value: 'department', label: 'Setor' },
  { value: 'occupation', label: 'Função' },
  { value: 'employee', label: 'Funcionário' },
];

/**
 * O limite gravado na medição é texto: o valor e a unidade juntos, como o laudo cita.
 * A norma de origem vai em campo próprio, porque é ela que diverge entre laboratórios.
 */
const valorDoLimite = (limite: HazardousAgentToleranceLimit): string =>
  [limite.toleranceLimit, limite.measurementUnit].filter(Boolean).join(' ');

const rotuloDoLimite = (limite: HazardousAgentToleranceLimit): string =>
  limite.source ? `${valorDoLimite(limite)} — ${limite.source}` : valorDoLimite(limite);

/**
 * O grau no catálogo de agentes é texto sem validação — a planilha do cliente chega a trazer
 * "0.2" —, e sugerir um valor desses deixaria o formulário com algo que o servidor recusa.
 */
const grauSugerido = (grauDoCatalogo: string | null): string =>
  UNHEALTHY_DEGREES.includes(grauDoCatalogo as UnhealthyDegree) ? grauDoCatalogo! : '';

const ROTULO_DA_TECNICA: Readonly<Record<string, string>> = {
  QUALITATIVE: 'Qualitativa',
  QUANTITATIVE: 'Quantitativa',
};

/**
 * A metodologia que a norma prescreve para o agente, escrita como o LTCAT a imprime (#464):
 * técnica por extenso, fundamento normativo e norma de higiene, pulando o que faltar.
 *
 * Sem técnica na fonte não há sugestão: fundamento e norma sozinhos não respondem "qual
 * técnica foi usada", e preencher o campo com eles seria pôr outra coisa no lugar da resposta.
 */
/** O método que a máscara do PGR imprime para os cinco parâmetros de conforto (#528). */
const TECNICA_DO_CONFORTO = 'Medição por pontos';

const tecnicaSugerida = (ficha: HazardousAgentSheet | null): string => {
  const tecnica = ROTULO_DA_TECNICA[ficha?.assessmentTypeNaFonte ?? ''] ?? '';

  if (tecnica === '') {
    return '';
  }

  return [tecnica, ficha?.assessmentBasis, ficha?.hygieneStandards]
    .filter((parte) => Boolean(parte))
    .join(' / ');
};

const SCOPE_TARGET_LABELS: Record<RiskApplicationScope, string> = {
  company: 'Empresa',
  unit: 'Unidade',
  department: 'Setor',
  occupation: 'Função',
  employee: 'Funcionário',
};

export const EnvironmentalMeasurementForm: React.FC<EnvironmentalMeasurementFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const isEditing = Boolean(initialData?.id);
  const { shouldShowCompanyField, effectiveCompanyId } = useCompanyContext();

  const [companies, setCompanies] = useState<Option[]>([]);
  const [equipments, setEquipments] = useState<Option[]>([]);
  const [users, setUsers] = useState<Option[]>([]);
  const [loadingDependencies, setLoadingDependencies] = useState(true);

  const [companyId, setCompanyId] = useState<number | null>(effectiveCompanyId);
  const [scope, setScope] = useState<RiskApplicationScope | ''>('');
  const [scopeId, setScopeId] = useState<number | null>(null);
  const [targets, setTargets] = useState<Option[]>([]);
  const [applications, setApplications] = useState<RiskApplication[]>([]);
  const [loadingApplications, setLoadingApplications] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm<EnvironmentalMeasurementFormData>({
    defaultValues: {
      riskApplicationId: initialData?.riskApplicationId ?? null,
      measurementEquipmentId: initialData?.measurementEquipmentId ?? null,
      value: initialData?.value === undefined || initialData?.value === null ? '' : String(initialData.value),
      unit: initialData?.unit ?? '',
      measuredAt: initialData?.measuredAt ? initialData.measuredAt.slice(0, 10) : today,
      technique: initialData?.technique ?? '',
      exposureTime: initialData?.exposureTime ?? '',
      responsibleUserId: initialData?.responsibleUserId ?? null,
      observations: initialData?.observations ?? '',
      exceedsLimit: initialData?.exceedsLimit ?? false,
      storedQuantity: initialData?.storedQuantity == null ? '' : String(initialData.storedQuantity),
      storedQuantityUnit: initialData?.storedQuantityUnit ?? '',
      transportedQuantity: initialData?.transportedQuantity == null ? '' : String(initialData.transportedQuantity),
      transportedQuantityUnit: initialData?.transportedQuantityUnit ?? '',
      riskArea: initialData?.riskArea ?? '',
      storageType: initialData?.storageType ?? '',
      hazardousFraming: initialData?.hazardousFraming ?? false,
      hazardousAnnex: initialData?.hazardousAnnex ?? '',
      unhealthyFraming: initialData?.unhealthyFraming ?? false,
      unhealthyDegreeApplied: initialData?.unhealthyDegreeApplied ?? '',
      appliedToleranceLimit: initialData?.appliedToleranceLimit ?? '',
      appliedToleranceLimitSource: initialData?.appliedToleranceLimitSource ?? '',
      quartzPercent: initialData?.quartzPercent == null ? '' : String(initialData.quartzPercent),
      assessmentType: initialData?.assessmentType ?? '',
      comfortParameter: initialData?.comfortParameter ?? '',
      measurementPoint: initialData?.measurementPoint ?? '',
    },
  });

  const selectedApplicationId = watch('riskApplicationId');
  const enquadrado = watch('hazardousFraming');
  const enquadradoInsalubre = watch('unhealthyFraming');
  const limiteAplicado = watch('appliedToleranceLimit');
  const origemDoLimite = watch('appliedToleranceLimitSource');
  const parametroDeConforto = watch('comfortParameter');
  const valorMedido = watch('value');

  // #417 — o agente do catálogo ligado ao risco medido, que é quem tem as opções de
  // limite. Na edição a medição não carrega essa ligação: vem do risco, por id.
  const [agenteDoRiscoEditado, setAgenteDoRiscoEditado] = useState<number | null>(null);
  const [opcoesDeLimite, setOpcoesDeLimite] = useState<HazardousAgentToleranceLimit[]>([]);
  const [fichaDoAgente, setFichaDoAgente] = useState<HazardousAgentSheet | null>(null);

  useEffect(() => {
    const loadDependencies = async () => {
      const [equipmentList, userList, companyList] = await Promise.all([
        measurementEquipmentService.getMeasurementEquipments({ limit: 1000 }).catch(() => null),
        userService.getUsers({ limit: 1000 }).catch(() => null),
        shouldShowCompanyField
          ? companyService.getCompaniesForDropdown().catch(() => [])
          : Promise.resolve<Option[]>([]),
      ]);

      setEquipments((equipmentList?.equipments ?? []).map((e) => ({ id: e.id, name: e.name })));
      setUsers((userList?.data ?? []).map((u) => ({ id: u.id, name: u.name })));
      setCompanies(companyList.map((c) => ({ id: c.id, name: c.name })));
      setLoadingDependencies(false);
    };

    loadDependencies();
  }, [shouldShowCompanyField]);

  // Alvos do nível escolhido. Empresa é o próprio alvo, então não tem lista.
  useEffect(() => {
    if (isEditing || !companyId || !scope || scope === 'company') {
      setTargets([]);
      return;
    }

    const loadTargets = async () => {
      if (scope === 'department') {
        const response = await departmentService
          .getDepartments({ limit: 1000, companyId: String(companyId) })
          .catch(() => null);
        setTargets((response?.data ?? []).map((d) => ({ id: Number(d.id), name: d.name })));
        return;
      }

      if (scope === 'occupation') {
        const response = await occupationService
          .getOccupations({ limit: 1000, filters: { companyId } })
          .catch(() => null);
        setTargets((response?.data ?? []).map((o) => ({ id: Number(o.id), name: o.name })));
        return;
      }

      const response = await employeeService
        .getEmployees({ limit: 1000, filters: { companyId } })
        .catch(() => null);
      setTargets((response?.data ?? []).map((e) => ({ id: Number(e.id), name: e.name })));
    };

    loadTargets();
  }, [isEditing, companyId, scope]);

  const effectiveScopeId = scope === 'company' ? companyId : scopeId;

  useEffect(() => {
    if (isEditing || !scope || !effectiveScopeId) {
      setApplications([]);
      return;
    }

    const loadApplications = async () => {
      setLoadingApplications(true);
      try {
        setApplications(await riskApplicationService.list(scope, effectiveScopeId));
      } catch {
        setApplications([]);
      } finally {
        setLoadingApplications(false);
      }
    };

    loadApplications();
  }, [isEditing, scope, effectiveScopeId]);

  // #459 — na edição, a fórmula vem do risco embutido na aplicação da medição, e não do cadastro
  // de riscos, que exige outra permissão. Só se pede quando a medição já tem quartzo gravado: é
  // o sinal de que o risco tem fórmula, e os demais riscos não ganham chamada nenhuma.
  const aplicacaoDaEdicao = useDadoPermitido<RiskApplication | null>(
    'risk_application.read',
    'Aplicação de risco',
    useCallback(
      () => riskApplicationService.getById(initialData?.riskApplicationId as number),
      [initialData?.riskApplicationId]
    ),
    { habilitado: Boolean(isEditing && initialData?.riskApplicationId && initialData.quartzPercent != null) }
  );

  const riskContext = useMemo<MeasuredRiskContext>(() => {
    if (initialData) {
      return {
        riskFactorName: initialData.riskFactorName,
        toleranceLimit: initialData.toleranceLimit,
        measurementUnit: initialData.measurementUnit,
        isHazardous: initialData.riskIsHazardous,
        isUnhealthy: initialData.riskIsUnhealthy,
        unhealthyDegree: initialData.riskUnhealthyDegree,
        hazardousAgentId: agenteDoRiscoEditado,
        formulaDoLimite: aplicacaoDaEdicao.dado?.risk?.formulaDoLimite ?? null,
      };
    }

    // A API serializa os BIGINT como texto; o Select devolve número. Compara como texto.
    const application = applications.find((a) => String(a.id) === String(selectedApplicationId));
    const risk = application?.risk;
    // O agente pode ser perigoso e insalubre ao mesmo tempo — a radiação ionizante é o caso —,
    // e aí os dois cartões de enquadramento aparecem juntos (#357).
    return {
      riskFactorName: risk?.riskFactorName ?? null,
      toleranceLimit: risk?.toleranceLimit ?? null,
      measurementUnit: risk?.measurementUnit ?? null,
      isHazardous: Boolean(risk?.hazardousAgent),
      isUnhealthy: Boolean(risk?.unhealthyAgent),
      unhealthyDegree: risk?.unhealthyDegree ?? null,
      hazardousAgentId: risk?.hazardousAgentId ?? null,
      formulaDoLimite: risk?.formulaDoLimite ?? null,
    };
  }, [initialData, applications, selectedApplicationId, agenteDoRiscoEditado, aplicacaoDaEdicao.dado]);

  // Medição de sílica já gravada tem quartzo e limite calculado, mesmo antes de a fórmula chegar.
  const calculando =
    !riskContext.isHazardous &&
    (Boolean(riskContext.formulaDoLimite) ||
      initialData?.quartzPercent != null ||
      initialData?.calculatedToleranceLimit != null);

  // Aplicação sem fórmula não tem % de quartzo: o que ficou digitado de uma aplicação anterior
  // não pode ir no envio (o servidor recusaria o campo).
  useEffect(() => {
    if (!calculando && getValues('quartzPercent')) setValue('quartzPercent', '');
  }, [calculando, getValues, setValue]);

  // Na edição, a ligação com o catálogo vem do risco — a medição não a carrega. Sem
  // permissão de leitura do risco, o limite volta a ser texto livre, e nada trava.
  //
  // A degradação já era intencional, mas vinha de um `catch` que engolia a recusa: a
  // chamada saía, voltava 403 e virava ruído no log do servidor para uma resposta que já
  // se sabia. A porta desta tela cobra `environmental_measurement.create/update`, e ler
  // risco cobra `occupational_risk.read` — por isso a pergunta vem antes do pedido.
  const riscoDaEdicao = useDadoPermitido<OccupationalRisk>(
    'occupational_risk.read',
    'Risco ocupacional',
    useCallback(
      () => occupationalRiskService.getOccupationalRiskById(initialData?.riskId as number),
      [initialData?.riskId]
    ),
    { habilitado: Boolean(isEditing && initialData?.riskId && !initialData.riskIsHazardous) }
  );

  useEffect(() => {
    setAgenteDoRiscoEditado(riscoDaEdicao.dado?.hazardousAgentId ?? null);
  }, [riscoDaEdicao.dado]);

  // Na NR-16 não há limite a escolher: a atividade está descrita no anexo ou não está.
  useEffect(() => {
    if (riskContext.isHazardous || !riskContext.hazardousAgentId) {
      setOpcoesDeLimite([]);
      setFichaDoAgente(null);
      return;
    }

    let ativo = true;
    hazardousAgentService
      .getSheet(riskContext.hazardousAgentId)
      .then((ficha) => {
        if (!ativo) return;
        setFichaDoAgente(ficha);
        // Com fórmula, o limite não se escolhe: o catálogo do agente é o mesmo para os três riscos.
        setOpcoesDeLimite(calculando ? [] : (ficha.toleranceLimits ?? []));
      })
      .catch(() => {
        if (!ativo) return;
        setFichaDoAgente(null);
        setOpcoesDeLimite([]);
      });

    return () => { ativo = false; };
  }, [riskContext.isHazardous, riskContext.hazardousAgentId, calculando]);

  // A unidade do resultado é a da fórmula; o servidor grava essa e ignora a digitada.
  const unidadeDaFormula = riskContext.formulaDoLimite?.unidade;
  useEffect(() => {
    if (unidadeDaFormula) setValue('unit', unidadeDaFormula);
  }, [unidadeDaFormula, setValue]);

  // Opção única já vem escolhida: 229 dos 400 agentes têm limite, e boa parte tem um só.
  useEffect(() => {
    if (opcoesDeLimite.length !== 1 || getValues('appliedToleranceLimit')) return;

    const unica = opcoesDeLimite[0]!;
    setValue('appliedToleranceLimit', valorDoLimite(unica));
    setValue('appliedToleranceLimitSource', unica.source ?? '');
  }, [opcoesDeLimite, getValues, setValue]);

  // A técnica do catálogo é valor inicial: entra só em campo vazio, nunca por cima da que
  // veio da medição nem da que o técnico digitou. Na medição de conforto ela não vale: a
  // metodologia do agente do risco não é a da medição por pontos da NR-17.
  useEffect(() => {
    const sugestao = tecnicaSugerida(fichaDoAgente);
    if (sugestao === '' || getValues('technique') || getValues('comfortParameter')) return;

    setValue('technique', sugestao);
  }, [fichaDoAgente, getValues, setValue]);

  const riscoDaAplicacao = applications.find(
    (aplicacao) => String(aplicacao.id) === String(selectedApplicationId)
  )?.riskId;
  const riscoParaCadastrar = initialData?.riskId ?? riscoDaAplicacao ?? null;
  // Pergunta pela rota de DESTINO, e não pela tela onde o link está: quem edita medição
  // sem ter o módulo de riscos veria o atalho e cairia na recusa do outro lado.
  const acessoAoRisco = useVeredito('/occupational-risks/:id/edit', pode('occupational_risk.update'));

  /**
   * Por que o campo veio como veio. Sem distinguir os casos, a ausência de sugestão na
   * periculosidade — onde ela é correta, porque a NR-16 não tem agente do catálogo — se lê
   * como defeito, e o técnico vai procurar um erro que não existe.
   */
  const avisoDaTecnica = (): React.ReactNode => {
    if (riskContext.isHazardous) {
      return 'Risco de periculosidade (NR-16): o enquadramento é por anexo da norma, e não há agente do catálogo com técnica a sugerir.';
    }

    if (riscoDaEdicao.carregando) {
      return '';
    }

    if (!riskContext.hazardousAgentId) {
      return (
        <>
          {'Este risco ainda não está ligado a um agente do catálogo, e por isso não há técnica sugerida. '}
          {!acessoAoRisco.liberado || riscoParaCadastrar === null ? (
            'Peça a um administrador para ligar o risco a um agente do catálogo.'
          ) : (
            <Link
              component={RouterLink}
              to={`/occupational-risks/${riscoParaCadastrar}/edit`}
              underline="always"
            >
              Abrir o cadastro do risco
            </Link>
          )}
        </>
      );
    }

    if (!fichaDoAgente) {
      return '';
    }

    return tecnicaSugerida(fichaDoAgente) === ''
      ? 'A fonte não define técnica para este agente: descreva a que foi usada na medição.'
      : 'Sugestão do catálogo do agente; troque se a medição usou outra técnica.';
  };

  const opcaoEscolhida = opcoesDeLimite.find(
    (opcao) => valorDoLimite(opcao) === limiteAplicado && (opcao.source ?? '') === origemDoLimite,
  );

  const applicationLabel = (application: RiskApplication): string => {
    const name = application.risk?.riskFactorName ?? `Aplicação #${application.id}`;
    if (!application.startDate) return name;
    return `${name} — desde ${new Date(application.startDate).toLocaleDateString('pt-BR')}`;
  };

  const handleScopeChange = (value: RiskApplicationScope | '') => {
    setScope(value);
    setScopeId(null);
    setValue('riskApplicationId', null);
  };

  if (loadingDependencies) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit((data) => onSubmit(data))} noValidate>
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Aplicação de risco"
          subheader={
            isEditing
              ? 'A aplicação de risco de uma medição já registrada não muda.'
              : 'A medição parte de um risco já aplicado a uma empresa, setor, função ou funcionário.'
          }
        />
        <Divider />
        <CardContent>
          {isEditing ? (
            <Box>
              <Typography variant="body1" fontWeight="medium">
                {riskContext.riskFactorName ?? 'Risco não identificado'}
              </Typography>
              {initialData?.scope && (
                <Chip
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  label={`Aplicado por ${SCOPE_TARGET_LABELS[initialData.scope].toLowerCase()}`}
                />
              )}
            </Box>
          ) : (
            <Grid container spacing={2}>
              {shouldShowCompanyField && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="empresa-label">Empresa</InputLabel>
                    <Select
                      labelId="empresa-label"
                      label="Empresa"
                      value={companyId ? String(companyId) : ''}
                      onChange={(e) => {
                        setCompanyId(e.target.value ? Number(e.target.value) : null);
                        setScopeId(null);
                        setValue('riskApplicationId', null);
                      }}
                    >
                      {companies.map((c) => (
                        <MenuItem key={c.id} value={String(c.id)}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="nivel-aplicacao-label">Nível de aplicação</InputLabel>
                  <Select
                    labelId="nivel-aplicacao-label"
                    label="Nível de aplicação"
                    value={scope}
                    onChange={(e) => handleScopeChange(e.target.value as RiskApplicationScope | '')}
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {scope !== '' && scope !== 'company' && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="alvo-aplicacao-label">{SCOPE_TARGET_LABELS[scope]}</InputLabel>
                    <Select
                      labelId="alvo-aplicacao-label"
                      label={SCOPE_TARGET_LABELS[scope]}
                      value={scopeId ? String(scopeId) : ''}
                      onChange={(e) => {
                        setScopeId(e.target.value ? Number(e.target.value) : null);
                        setValue('riskApplicationId', null);
                      }}
                    >
                      {targets.map((t) => (
                        <MenuItem key={t.id} value={String(t.id)}>
                          {t.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid size={12}>
                <Controller
                  name="riskApplicationId"
                  control={control}
                  rules={{ required: 'Selecione a aplicação de risco medida' }}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" error={Boolean(errors.riskApplicationId)}>
                      <InputLabel id="aplicacao-risco-label">Aplicação de risco</InputLabel>
                      <Select
                        labelId="aplicacao-risco-label"
                        label="Aplicação de risco"
                        value={field.value ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                        disabled={loadingApplications || applications.length === 0}
                      >
                        {applications.map((application) => (
                          <MenuItem key={application.id} value={String(application.id)}>
                            {applicationLabel(application)}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color={errors.riskApplicationId ? 'error' : 'text.secondary'} sx={{ mt: 0.5 }}>
                        {errors.riskApplicationId?.message ??
                          (effectiveScopeId && applications.length === 0 && !loadingApplications
                            ? 'Nenhum risco aplicado a este alvo. Aplique o risco em Riscos Ocupacionais antes de medir.'
                            : 'Escolha o nível e o alvo para listar os riscos já aplicados.')}
                      </Typography>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardHeader title="Medição" subheader="Valores efetivamente medidos em campo" />
        <Divider />
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: parametroDeConforto ? 6 : 12 }}>
              <Controller
                name="comfortParameter"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel id="parametro-conforto-label">Parâmetro de conforto</InputLabel>
                    <Select
                      {...field}
                      labelId="parametro-conforto-label"
                      label="Parâmetro de conforto"
                      value={field.value ?? ''}
                      onChange={(event) => {
                        const codigo = event.target.value;
                        field.onChange(codigo);
                        const parametro = COMFORT_PARAMETERS.find((opcao) => opcao.code === codigo);
                        if (!parametro) return;
                        setValue('unit', parametro.unit);
                        // Troca só o que o formulário sugeriu; o que o técnico digitou fica.
                        const tecnica = getValues('technique') ?? '';
                        if (tecnica === '' || tecnica === tecnicaSugerida(fichaDoAgente)) {
                          setValue('technique', TECNICA_DO_CONFORTO);
                        }
                      }}
                    >
                      <MenuItem value="">Não é medição de conforto</MenuItem>
                      {COMFORT_PARAMETERS.map((parametro) => (
                        <MenuItem key={parametro.code} value={parametro.code}>
                          {parametro.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Medição de conforto (NR-17) sai no quadro de conforto do PGR, comparada ao
                      limite fixo da norma.
                    </Typography>
                  </FormControl>
                )}
              />
            </Grid>

            {parametroDeConforto && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="measurementPoint"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Ponto medido"
                      size="small"
                      fullWidth
                      helperText="Onde a medição foi feita, como a sala ou o posto de trabalho."
                      inputProps={{ maxLength: 255 }}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 3 }}>
              <Controller
                name="value"
                control={control}
                rules={{ required: riskContext.isHazardous ? false : 'Informe o valor medido' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Valor medido"
                    type="number"
                    size="small"
                    fullWidth
                    error={Boolean(errors.value)}
                    helperText={
                      errors.value?.message ??
                      (riskContext.isHazardous
                        ? 'Opcional: a NR-16 enquadra a atividade em vez de medir uma grandeza.'
                        : calculando
                          ? 'Resultado do laboratório, com até 4 casas decimais.'
                          : undefined)
                    }
                    inputProps={{ step: 'any' }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Controller
                name="unit"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Unidade"
                    size="small"
                    fullWidth
                    disabled={Boolean(unidadeDaFormula)}
                    helperText={unidadeDaFormula ? 'A unidade vem da fórmula do limite.' : undefined}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Controller
                name="measuredAt"
                control={control}
                rules={{ required: 'Informe a data da medição' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Data da medição"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(errors.measuredAt)}
                    helperText={errors.measuredAt?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Controller
                name="exposureTime"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Tempo de exposição" size="small" fullWidth />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="technique"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Técnica utilizada"
                    size="small"
                    fullWidth
                    helperText={avisoDaTecnica()}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="assessmentType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel id="natureza-avaliacao-label">Natureza da avaliação</InputLabel>
                    <Select
                      {...field}
                      labelId="natureza-avaliacao-label"
                      label="Natureza da avaliação"
                      value={field.value ?? ''}
                    >
                      <MenuItem value="">Não informada</MenuItem>
                      {MEASUREMENT_ASSESSMENT_TYPES.map((natureza) => (
                        <MenuItem key={natureza} value={natureza}>
                          {MEASUREMENT_ASSESSMENT_TYPE_LABELS[natureza]}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Qualitativa enquanto a empresa não mede; quantitativa quando o laboratório
                      entrega o resultado.
                    </Typography>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="responsibleUserId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel id="responsavel-label">Responsável pela medição</InputLabel>
                    <Select
                      labelId="responsavel-label"
                      label="Responsável pela medição"
                      value={field.value ? String(field.value) : ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                    >
                      <MenuItem value="">Não informado</MenuItem>
                      {users.map((user) => (
                        <MenuItem key={user.id} value={String(user.id)}>
                          {user.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="measurementEquipmentId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel id="equipamento-label">Equipamento de medição</InputLabel>
                    <Select
                      labelId="equipamento-label"
                      label="Equipamento de medição"
                      value={field.value ? String(field.value) : ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                    >
                      <MenuItem value="">Sem equipamento</MenuItem>
                      {equipments.map((equipment) => (
                        <MenuItem key={equipment.id} value={String(equipment.id)}>
                          {equipment.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Opcional: a medição pode ser registrada sem equipamento cadastrado.
                    </Typography>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="observations"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Observações" size="small" fullWidth multiline rows={3} />
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {riskContext.isHazardous && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Quantidades e área de risco"
            subheader="O que foi levantado em campo sobre o produto e o alcance da atividade."
          />
          <Divider />
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 8, md: 3 }}>
                <Controller
                  name="storedQuantity"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Quantidade armazenada"
                      type="number"
                      size="small"
                      fullWidth
                      inputProps={{ step: 'any' }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 4, md: 2 }}>
                <Controller
                  name="storedQuantityUnit"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Unidade" size="small" fullWidth />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 8, md: 3 }}>
                <Controller
                  name="transportedQuantity"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Quantidade transportada"
                      type="number"
                      size="small"
                      fullWidth
                      inputProps={{ step: 'any' }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 4, md: 2 }}>
                <Controller
                  name="transportedQuantityUnit"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Unidade" size="small" fullWidth />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="riskArea"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Área de risco"
                      size="small"
                      fullWidth
                      placeholder="Raio de 7,5 m do tanque"
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="storageType"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Tipo de armazenamento" size="small" fullWidth />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {riskContext.isHazardous ? (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Enquadramento"
            subheader="Na NR-16 não há valor a comparar: a atividade está descrita no anexo ou não está."
          />
          <Divider />
          <CardContent>
            <Controller
              name="hazardousFraming"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        // Anexo sem enquadramento é dado que o laudo não sabe imprimir,
                        // e o servidor recusa o par incompleto.
                        if (!e.target.checked) setValue('hazardousAnnex', '');
                      }}
                    />
                  }
                  label="A atividade está descrita no anexo da NR-16"
                />
              )}
            />

            <Box sx={{ mt: 2, maxWidth: 420 }}>
              <Controller
                name="hazardousAnnex"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" disabled={!enquadrado}>
                    <InputLabel id="anexo-nr16-label">Anexo da NR-16</InputLabel>
                    <Select
                      {...field}
                      labelId="anexo-nr16-label"
                      label="Anexo da NR-16"
                      value={field.value ?? ''}
                    >
                      <MenuItem value="">Não informado</MenuItem>
                      {NR16_ANNEXES.map((anexo) => (
                        <MenuItem key={anexo} value={anexo}>
                          {anexo}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      {enquadrado
                        ? 'O anexo é o que o laudo cita como fundamento.'
                        : 'Disponível depois de marcar o enquadramento.'}
                    </Typography>
                  </FormControl>
                )}
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Enquadrada, a atividade dá direito ao adicional de periculosidade de 30% sobre o
              salário base.
            </Typography>
          </CardContent>
        </Card>
      ) : calculando ? (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Limite de tolerância"
            subheader="O sistema calcula o limite pelo % de quartzo e diz em que faixa o resultado ficou."
          />
          <Divider />
          <CardContent>
            <Controller
              name="quartzPercent"
              control={control}
              rules={{
                validate: (texto) => {
                  if (!texto?.trim()) return 'Informe o % de quartzo para calcular o limite.';
                  const numero = Number(texto);
                  return Number.isFinite(numero) && numero >= 0 && numero <= 100
                    ? true
                    : 'O % de quartzo deve ser um número de 0 a 100.';
                },
              }}
              render={({ field }) => (
                <BlocoDoLimiteCalculado
                  formula={riskContext.formulaDoLimite ?? null}
                  quartzo={field.value ?? ''}
                  onQuartzoChange={field.onChange}
                  resultado={valorMedido}
                  limiteGravado={initialData?.calculatedToleranceLimit ?? null}
                  unidade={initialData?.unit ?? null}
                  erro={errors.quartzPercent?.message}
                />
              )}
            />
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Excedente"
            subheader="Quem decide é quem mediu: o sistema não compara o valor medido com o limite."
          />
          <Divider />
          <CardContent>
            <Typography variant="body2">
              {limiteAplicado
                ? `Limite de tolerância aplicado: ${limiteAplicado}`
                : riskContext.toleranceLimit
                  ? `Limite de tolerância do risco: ${riskContext.toleranceLimit}`
                  : 'Limite de tolerância não informado no cadastro do risco.'}
            </Typography>
            {limiteAplicado && origemDoLimite && (
              <Typography variant="body2" color="text.secondary">
                Norma de origem: {origemDoLimite}
              </Typography>
            )}
            {!limiteAplicado && riskContext.measurementUnit && (
              <Typography variant="body2" color="text.secondary">
                Unidade de medida do risco: {riskContext.measurementUnit}
              </Typography>
            )}

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                {opcoesDeLimite.length > 0 ? (
                  <FormControl fullWidth size="small">
                    <InputLabel id="limite-aplicado-label">Limite de tolerância aplicado</InputLabel>
                    <Select
                      labelId="limite-aplicado-label"
                      label="Limite de tolerância aplicado"
                      value={opcaoEscolhida ? String(opcaoEscolhida.id) : ''}
                      onChange={(e) => {
                        const opcao = opcoesDeLimite.find((item) => String(item.id) === e.target.value);
                        setValue('appliedToleranceLimit', opcao ? valorDoLimite(opcao) : '');
                        setValue('appliedToleranceLimitSource', opcao?.source ?? '');
                      }}
                    >
                      <MenuItem value="">Não informado</MenuItem>
                      {opcoesDeLimite.map((opcao) => (
                        <MenuItem key={opcao.id} value={String(opcao.id)}>
                          {rotuloDoLimite(opcao)}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      O laudo do laboratório é quem diz qual das normas valeu nesta medição.
                    </Typography>
                  </FormControl>
                ) : (
                  <Controller
                    name="appliedToleranceLimit"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Limite de tolerância aplicado"
                        size="small"
                        fullWidth
                        helperText="O catálogo não traz limite para este agente: informe o que o laudo usou."
                      />
                    )}
                  />
                )}
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="appliedToleranceLimitSource"
                  control={control}
                  rules={{
                    validate: (valor, valores) =>
                      !valores.appliedToleranceLimit?.trim() || valor?.trim()
                        ? true
                        : 'Informe a norma de origem do limite aplicado.',
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Norma de origem do limite"
                      size="small"
                      fullWidth
                      error={Boolean(errors.appliedToleranceLimitSource)}
                      helperText={
                        errors.appliedToleranceLimitSource?.message ?? 'Ex.: NR-15, anexo 11 · ACGIH'
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Controller
              name="exceedsLimit"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="A medição excede o limite de tolerância"
                />
              )}
            />
          </CardContent>
        </Card>
      )}

      {riskContext.isUnhealthy && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Enquadramento de insalubridade"
            subheader="Quem enquadra é quem avaliou: o grau do catálogo é só o ponto de partida."
          />
          <Divider />
          <CardContent>
            <Controller
              name="unhealthyFraming"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        // Grau sem enquadramento é par incompleto, e o servidor o recusa;
                        // ao marcar, o grau do catálogo entra como sugestão a confirmar.
                        setValue(
                          'unhealthyDegreeApplied',
                          e.target.checked
                            ? getValues('unhealthyDegreeApplied') || grauSugerido(riskContext.unhealthyDegree)
                            : '',
                        );
                      }}
                    />
                  }
                  label="A atividade está enquadrada como insalubre (NR-15)"
                />
              )}
            />

            <Box sx={{ mt: 2, maxWidth: 420 }}>
              <Controller
                name="unhealthyDegreeApplied"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" disabled={!enquadradoInsalubre}>
                    <InputLabel id="grau-insalubridade-label">Grau de insalubridade aplicado</InputLabel>
                    <Select
                      {...field}
                      labelId="grau-insalubridade-label"
                      label="Grau de insalubridade aplicado"
                      value={field.value ?? ''}
                    >
                      <MenuItem value="">Não informado</MenuItem>
                      {UNHEALTHY_DEGREES.map((grau) => (
                        <MenuItem key={grau} value={grau}>
                          {OCC_UNHEALTHY_DEGREE_LABELS[grau]}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      {enquadradoInsalubre
                        ? 'É este grau que o quadro de insalubridade imprime.'
                        : 'Disponível depois de marcar o enquadramento.'}
                    </Typography>
                  </FormControl>
                )}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" startIcon={<CancelIcon />} onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={loading}>
          Salvar
        </Button>
      </Box>
    </Box>
  );
};

export default EnvironmentalMeasurementForm;
